/**
 * Utómunka egyetlen teljes képernyős menetben.
 *
 * Ez a réteg köti vizuálisan össze az egész játékot: a jelenet csökkentett
 * felbontású célpuffere, a színmélység-csökkentés Bayer-ditheringgel, a
 * vignetta, a filmszemcse és a sebzés-villanás együtt adják azt a
 * „nem mai" képet, amitől a procedurális assetek stílusnak látszanak,
 * nem szegénységnek.
 *
 * Gazda: C
 */

import * as THREE from 'three'

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`

const FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tScene;
uniform vec2  uResolution;
uniform float uTime;
uniform float uVignette;
uniform float uGrain;
uniform float uLevels;       // színszintek csatornánként
uniform float uAberration;
uniform vec3  uFlashColor;
uniform float uFlashStrength;
uniform float uSaturation;
uniform float uContrast;
uniform float uScanline;
uniform float uLift;        // fekete-emelés: a sötét részek se legyenek vakok
uniform float uCurve;       // S-görbe erőssége
uniform float uPivot;       // a görbe fordulópontja
uniform float uHurt;         // 0..1, alacsony élet — a szélek vörösödnek

varying vec2 vUv;

// 4x4 Bayer-mátrix, ugyanaz, mint a textúra-kvantálásban — így a képernyő
// és a felületek dithering-mintázata egy rendszert alkot.
float bayer(vec2 p) {
  int x = int(mod(p.x, 4.0));
  int y = int(mod(p.y, 4.0));
  int i = y * 4 + x;
  float m[16];
  m[0]=0.0;  m[1]=8.0;  m[2]=2.0;  m[3]=10.0;
  m[4]=12.0; m[5]=4.0;  m[6]=14.0; m[7]=6.0;
  m[8]=3.0;  m[9]=11.0; m[10]=1.0; m[11]=9.0;
  m[12]=15.0;m[13]=7.0; m[14]=13.0;m[15]=5.0;
  for (int k = 0; k < 16; k++) {
    if (k == i) return m[k] / 16.0 - 0.5;
  }
  return 0.0;
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec2 uv = vUv;
  vec2 centered = uv - 0.5;
  float r2 = dot(centered, centered);

  // Enyhe kromatikus aberráció a képszélek felé — optikai, nem digitális érzet.
  vec2 ab = centered * uAberration * r2;
  vec3 col;
  col.r = texture2D(tScene, uv + ab).r;
  col.g = texture2D(tScene, uv).g;
  col.b = texture2D(tScene, uv - ab).b;

  // Telítettség
  float luma = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(vec3(luma), col, uSaturation);

  // Filmes toe és váll: az alsó tartomány kap egy biztos, de még sötét
  // alapszintet, miközben a középtónusok gyorsan szétválnak, a csúcsok pedig
  // fokozatosan telítődnek. Ez nem lineáris kontraszt, ezért nem vágja le a
  // részletet sem a falak árnyékában, sem a lámpák körül.
  //
  // A lineáris kontraszt-szorzó ennél a képnél nem működik: vagy feketébe
  // nyomja a sötét kétharmadot, vagy kimossa a világosat. Az S-görbe a
  // pivot körüli KÖZÉPTÓNUSOKAT húzza szét — ott, ahol a fal, a padló és
  // az ellenfél sziluettje van —, a két végét viszont kíméli, így a mély
  // árnyék marad árnyék, a lámpa marad lámpa. Ettől lesz a képnek mélysége.
  vec3 shaped = uLift + (1.0 - uLift) * (1.0 - exp(-uContrast * pow(max(col, 0.0), vec3(uCurve))));
  col = mix(col, shaped, clamp(uPivot, 0.0, 1.0));

  // Sebzés / felvétel villanás
  col = mix(col, uFlashColor, clamp(uFlashStrength, 0.0, 1.0));

  // Alacsony élet: a képszélek vörösbe fordulnak és pulzálnak.
  float pulse = 0.5 + 0.5 * sin(uTime * 4.0);
  col = mix(col, vec3(0.45, 0.02, 0.03), uHurt * smoothstep(0.05, 0.32, r2) * (0.45 + 0.35 * pulse));

  // Vignetta
  col *= 1.0 - uVignette * smoothstep(0.15, 0.75, r2);

  // Halvány pásztázó vonalak — csak sejtetve, hogy ne fárassza a szemet.
  col *= 1.0 - uScanline * (0.5 + 0.5 * sin(gl_FragCoord.y * 3.14159));

  // Filmszemcse
  float n = hash(gl_FragCoord.xy + vec2(uTime * 61.0, uTime * 37.0));
  col += (n - 0.5) * uGrain;

  // Színmélység-csökkentés Bayer-ditheringgel
  float d = bayer(gl_FragCoord.xy) / uLevels;
  col = floor((col + d) * uLevels + 0.5) / uLevels;

  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`

export interface PostFxSettings {
  /** Belső renderfelbontás aránya (0.5..1). A retro-élesség kulcsa. */
  renderScale: number
  vignette: number
  grain: number
  levels: number
  aberration: number
  saturation: number
  contrast: number
  scanline: number
  /** Fekete-emelés 0..0.15 — a sötét részek olvashatósága. */
  lift: number
  /** S-görbe erőssége 0..1. */
  curve: number
  /** A kontraszt- és görbeszámítás fordulópontja. */
  pivot: number
}

export const DEFAULT_POSTFX: PostFxSettings = {
  renderScale: 0.78,
  vignette: 0.3,
  grain: 0.04,
  levels: 24,
  aberration: 0.0035,
  saturation: 1.16,
  contrast: 12,
  scanline: 0.018,
  lift: 0.08,
  curve: 1.9,
  pivot: 1,
}

export class PostFx {
  private target: THREE.WebGLRenderTarget
  private scene = new THREE.Scene()
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  private material: THREE.ShaderMaterial
  private settings: PostFxSettings

  /** Villanás: a játéklogika ezt hívja sebzésnél/felvételnél. */
  private flashColor = new THREE.Color(1, 0, 0)
  private flashStrength = 0

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    settings: PostFxSettings = DEFAULT_POSTFX,
  ) {
    this.settings = { ...settings }
    const size = new THREE.Vector2()
    renderer.getDrawingBufferSize(size)

    this.target = new THREE.WebGLRenderTarget(
      Math.max(1, Math.floor(size.x * this.settings.renderScale)),
      Math.max(1, Math.floor(size.y * this.settings.renderScale)),
      {
        minFilter: THREE.LinearFilter,
        // Nagyításnál nearest: ettől lesz karakteres pixelrács, nem mosott kép.
        magFilter: THREE.NearestFilter,
        type: THREE.HalfFloatType,
        colorSpace: THREE.SRGBColorSpace,
      },
    )

    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        tScene: { value: this.target.texture },
        uResolution: { value: new THREE.Vector2(size.x, size.y) },
        uTime: { value: 0 },
        uVignette: { value: this.settings.vignette },
        uGrain: { value: this.settings.grain },
        uLevels: { value: this.settings.levels },
        uAberration: { value: this.settings.aberration },
        uFlashColor: { value: new THREE.Vector3(1, 0, 0) },
        uFlashStrength: { value: 0 },
        uSaturation: { value: this.settings.saturation },
        uContrast: { value: this.settings.contrast },
        uScanline: { value: this.settings.scanline },
        uLift: { value: this.settings.lift },
        uCurve: { value: this.settings.curve },
        uPivot: { value: this.settings.pivot },
        uHurt: { value: 0 },
      },
    })

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material)
    quad.frustumCulled = false
    this.scene.add(quad)
  }

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width * this.settings.renderScale))
    const h = Math.max(1, Math.floor(height * this.settings.renderScale))
    this.target.setSize(w, h)
    this.material.uniforms.uResolution.value.set(width, height)
  }

  setRenderScale(scale: number): void {
    this.settings.renderScale = Math.max(0.4, Math.min(1, scale))
    const size = new THREE.Vector2()
    this.renderer.getDrawingBufferSize(size)
    this.setSize(size.x, size.y)
  }

  flash(color: number, strength: number): void {
    // A meglévő villanást nem törli, hanem a nagyobbik marad — így egy
    // gyors sorozat nem villog összevissza.
    if (strength >= this.flashStrength) {
      this.flashColor.setHex(color)
      this.flashStrength = Math.min(0.85, strength)
    }
  }

  /** Keretenként: a villanás lecsengése és a shader-idő. */
  update(dtReal: number, time: number, hurt: number): void {
    this.flashStrength = Math.max(0, this.flashStrength - dtReal * 2.6)
    const u = this.material.uniforms
    u.uTime.value = time
    u.uFlashStrength.value = this.flashStrength
    u.uFlashColor.value.set(this.flashColor.r, this.flashColor.g, this.flashColor.b)
    u.uHurt.value = hurt
  }

  /**
   * A belső célpufferbe rajzol. A hívó több menetet is végezhet (világ,
   * majd letisztított mélységgel a kézifegyver) — ezért kapja meg a
   * renderert, nem egy kész jelenetet.
   */
  renderTo(draw: (renderer: THREE.WebGLRenderer) => void): void {
    this.renderer.setRenderTarget(this.target)
    this.renderer.clear()
    draw(this.renderer)
    this.renderer.setRenderTarget(null)
  }

  /** A belső puffert a képernyőre másolja az effektekkel. */
  present(): void {
    this.renderer.render(this.scene, this.camera)
  }

  dispose(): void {
    this.target.dispose()
    this.material.dispose()
  }
}
